import React, { useState, useEffect, useCallback } from 'react';
import { clientsApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  X,
  Check,
  Warning,
  Phone,
  Envelope
} from '@phosphor-icons/react';

const statusColors = {
  activo: 'badge-success',
  atrasado: 'badge-warning',
  en_cobranza: 'badge-danger',
  vencido: 'badge-danger'
};

const statusLabels = {
  activo: 'Activo',
  atrasado: 'Atrasado',
  en_cobranza: 'En Cobranza',
  vencido: 'Vencido'
};

const Clients = () => {
  const { canEdit, canManage } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'PF',
    rfc: '',
    phone: '',
    email: '',
    address: ''
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await clientsApi.getAll();
      setClients(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      if (editingClient) {
        await clientsApi.update(editingClient.id, formData);
      } else {
        await clientsApi.create(formData);
      }
      setShowModal(false);
      setEditingClient(null);
      setFormData({ name: '', type: 'PF', rfc: '', phone: '', email: '', address: '' });
      loadClients();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      type: client.type || 'PF',
      rfc: client.rfc || '',
      phone: client.phone || '',
      email: client.email || '',
      address: client.address || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    try {
      await clientsApi.delete(id);
      loadClients();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleStatusChange = async (clientId, newStatus) => {
    try {
      await clientsApi.update(clientId, { status: newStatus });
      setClients(clients.map(c => c.id === clientId ? { ...c, status: newStatus } : c));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const filteredClients = clients.filter(
    (c) =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm) ||
      c.rfc?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Clientes</h1>
          <p className="text-[#64748B]">{clients.length} clientes registrados</p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditingClient(null);
              setFormData({ name: '', type: 'PF', rfc: '', phone: '', email: '', address: '' });
              setShowModal(true);
            }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-client-btn"
          >
            <Plus size={18} />
            Nuevo Cliente
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger flex items-center gap-2">
          <Warning size={20} />
          {error}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nombre, teléfono o RFC..."
          className="input pl-10"
          data-testid="search-clients-input"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="clients-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>RFC</th>
                <th>Teléfono</th>
                <th>Email</th>
                <th>Estatus</th>
                {canEdit && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 7 : 6} className="text-center py-8 text-[#64748B]">
                    No se encontraron clientes
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td className="font-medium">{client.name}</td>
                    <td>
                      <span className="badge badge-neutral">{client.type}</span>
                    </td>
                    <td className="font-mono text-sm">{client.rfc || '-'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Phone size={14} className="text-[#64748B]" />
                        {client.phone}
                      </div>
                    </td>
                    <td>
                      {client.email ? (
                        <div className="flex items-center gap-1">
                          <Envelope size={14} className="text-[#64748B]" />
                          {client.email}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <select
                        value={client.status || 'activo'}
                        onChange={(e) => handleStatusChange(client.id, e.target.value)}
                        className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer ${statusColors[client.status] || 'badge-neutral'}`}
                        data-testid={`status-client-${client.id}`}
                      >
                        {Object.entries(statusLabels).map(([val, label]) => (
                          <option key={val} value={val}>{label}</option>
                        ))}
                      </select>
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(client)}
                            className="p-2 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded"
                            data-testid={`edit-client-${client.id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          {canManage && (
                            <button
                              onClick={() => handleDelete(client.id)}
                              className="p-2 text-[#64748B] hover:text-[#E11D48] hover:bg-red-50 rounded"
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="client-modal">
          <div className="modal-content animate-fade-in">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">
                  {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="alert alert-danger text-sm">{formError}</div>
              )}

              <div>
                <label className="label">Nombre Completo *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  required
                  data-testid="client-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Tipo de Cliente</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="input"
                    data-testid="client-type-select"
                  >
                    <option value="PF">Persona Física</option>
                    <option value="PM">Persona Moral</option>
                  </select>
                </div>
                <div>
                  <label className="label">RFC</label>
                  <input
                    type="text"
                    value={formData.rfc}
                    onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                    className="input font-mono"
                    placeholder="XXXX000000XXX"
                    data-testid="client-rfc-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Teléfono *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                    required
                    placeholder="55 1234 5678"
                    data-testid="client-phone-input"
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    placeholder="correo@ejemplo.com"
                    data-testid="client-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="label">Dirección</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input"
                  rows={2}
                  data-testid="client-address-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-client-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={18} />
                  )}
                  {editingClient ? 'Guardar Cambios' : 'Crear Cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
